import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import entityChildrenSelector from "../selectors/entityChildrenSelector";
import { EntityTypeRelation } from "../interfaces/entity";
import { useGetNormalizedAnnotationBatchMutation } from "../store/configMapApi";
import { updateAnnotationNormalizationInState } from "../../annotation/store/annotationSlice";
import useAnnotationLoading from "../../annotation/hooks/useAnnotationLoading";
import {
  EntityAnnotation,
  EntityAnnotationBatchToBeNormalizedDto,
  EntityAnnotationDto,
} from "../../annotation/interfaces/annotation";
import { notification } from "antd";
import flatEntityListSelector from "../selectors/flatEntityListSelector";
import { useTranslation } from "react-i18next";

const useEntityParentNormalization = (
  childrenIds: Array<EntityTypeRelation> | null,
  entityAnnotation: EntityAnnotation,
) => {
  const { t } = useTranslation("annotations");
  const dispatch = useAppDispatch();
  const { setLoading } = useAnnotationLoading();

  const previousValue = useRef("");
  const isRunning = useRef(false);

  const entities = useAppSelector(flatEntityListSelector);
  const childrenAnnotations = useAppSelector((state) =>
    entityChildrenSelector(state, { childrenIds }),
  );

  const metaData = useAppSelector(
    (state) => state.documentReducer.activeDocument?.metaData,
  );

  const documentSetId = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet?.id,
  );

  const [getNormalizedAnnotationBatch] =
    useGetNormalizedAnnotationBatchMutation();

  const valueToUse = useMemo(() => {
    if (entityAnnotation.entityAnnotationNormalization) {
      if (
        entityAnnotation.entityAnnotationNormalization.normalizedMethod?.toLowerCase() ===
        "manual"
      ) {
        return entityAnnotation.entityAnnotationNormalization.normalizedValue;
      }
    }

    return entityAnnotation.value;
  }, [entityAnnotation]);

  const updateEntityChildren = useCallback(async () => {
    if (valueToUse === previousValue.current) {
      return;
    }

    if (!childrenAnnotations.length) {
      previousValue.current = valueToUse;
      return;
    }

    if (!valueToUse) {
      previousValue.current = valueToUse;
      return;
    }

    const toUpdateIds = childrenAnnotations.map((ea) => ea.id);

    try {
      isRunning.current = true;
      const entityAnnotationsBody: EntityAnnotationBatchToBeNormalizedDto = {
        entityAnnotations: childrenAnnotations.map((a): EntityAnnotationDto => {
          if (a.entityAnnotationNormalization) {
            return {
              id: a.id,
              value: a.values.join(" "),
              normalizedValue: {
                value: a.entityAnnotationNormalization.normalizedValue,
                method: a.entityAnnotationNormalization.normalizedMethod || "",
              },
              index: a.index || 1,
              entityNormalizations: a.entity.entityNormalizations || [],
              entityTypeId: a.entity.id,
              multipleGroupBlocks: a.entity.multipleGroupBlocks || false,
              parentValue: valueToUse,
            };
          }
          return {
            id: a.id,
            value: a.values.join(" "),
            index: a.index || 1,
            entityNormalizations: a.entity.entityNormalizations || [],
            entityTypeId: a.entity.id,
            multipleGroupBlocks: a.entity.multipleGroupBlocks || false,
            parentValue: valueToUse,
          };
        }),
        metaData: metaData,
        documentSetId: documentSetId,
      };

      setLoading(toUpdateIds, true);

      const found = entities.find(
        (e) =>
          e.entityType.id.toLowerCase() ===
          childrenAnnotations[0].entity.id.toLowerCase(),
      );

      const url =
        (found?.entityType?.entityNormalizations || [])[0]
          ?.normalizationStrategy?.urlBatch || null;

      const normalizedAnnotations = await getNormalizedAnnotationBatch({
        url,
        entityAnnotationsDto: entityAnnotationsBody,
      }).unwrap();

      normalizedAnnotations.forEach((normalizedAnnotation) => {
        if (!normalizedAnnotation.id) {
          return;
        }

        const multipleGroupBlocks = childrenAnnotations.find(
          (a) => a.id === normalizedAnnotation.id!,
        )!.multipleGroupBlocks;

        dispatch(
          updateAnnotationNormalizationInState({
            id: normalizedAnnotation.id,
            newValue: normalizedAnnotation.normalizedValue,
            method: normalizedAnnotation.normalizedMethod,
            options: normalizedAnnotation.options,
            multipleGroupBlocks: multipleGroupBlocks,
            isByUser: true,
            errorType: normalizedAnnotation.errorType,
          }),
        );
      });
      previousValue.current = valueToUse;
    } catch {
      notification.error({
        title: t("normalizationError"),
      });
      return;
    } finally {
      isRunning.current = false;
      previousValue.current = valueToUse;
      setLoading(toUpdateIds, false);
    }
  }, [
    t,
    dispatch,
    childrenAnnotations,
    valueToUse,
    metaData,
    documentSetId,
    getNormalizedAnnotationBatch,
    entities,
    setLoading,
  ]);

  useEffect(() => {
    if (isRunning.current) {
      return;
    }

    updateEntityChildren();
  }, [updateEntityChildren]);
};

export default useEntityParentNormalization;
